import * as z from 'zod';

export const CreateAddOnRequest = z.object({
  addOn: z
    .intersection(
      z.object({
        _id: z
          .string()
          .describe('Add-on ID.')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        revision: z
          .string()
          .regex(/^-?\d+$/, 'Must be a valid Int64 string')
          .describe(
            'Revision number, which increments by 1 each time the add-on is updated.\nTo prevent conflicting changes, the current revision must be passed when updating the add-on.\n\nIgnored when creating an add-on.'
          )
          .optional()
          .nullable(),
        _createdDate: z
          .date()
          .describe(
            'Date and time the add-on was created in `YYYY-MM-DDThh:mm:ss.sssZ` format.'
          )
          .optional()
          .nullable(),
        _updatedDate: z
          .date()
          .describe(
            'Date and time the add-on was last updated in `YYYY-MM-DDThh:mm:ss.sssZ` format.'
          )
          .optional()
          .nullable(),
        name: z
          .string()
          .describe('Display name of the add-on shown to customers.')
          .min(1)
          .max(100)
          .optional()
          .nullable(),
        price: z
          .object({
            value: z
              .string()
              .describe(
                'Add-on price amount as a decimal string with period as decimal separator. For example, `3.99` for a $3.99 add-on or `15.00` for a $15.00 add-on.'
              )
              .optional(),
            currency: z
              .string()
              .describe(
                'Currency code for the add-on price. Must be a valid [ISO 4217](https://en.wikipedia.org/wiki/ISO_4217) currency code. For example, `USD` for US dollars or `EUR` for euros.'
              )
              .optional(),
            formattedValue: z
              .string()
              .describe(
                'Add-on price amount as a decimal string in local format for display purposes. For example, `$3.99` for US format or `3,99 €` for European format.'
              )
              .max(50)
              .optional()
              .nullable(),
          })
          .describe('Pricing information for the add-on.')
          .optional(),
        taxGroup: z
          .string()
          .describe(
            "ID of the [tax group](https://dev.wix.com/docs/rest/business-solutions/e-commerce/extensions/tax/tax-groups/introduction) to categorize the add-on for tax calculation in [Wix eCommerce](https://dev.wix.com/docs/rest/business-solutions/e-commerce/introduction).\nLearn more about [how Wix eCommerce implements tax calculation](https://dev.wix.com/docs/rest/business-solutions/e-commerce/extensions/tax/introduction).\n\nThis field doesn't apply to custom checkout/billing implementations."
          )
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        extendedFields: z
          .object({
            namespaces: z
              .record(z.string(), z.record(z.string(), z.any()))
              .describe(
                'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
              )
              .optional(),
          })
          .describe(
            'Data extensions enabling users to save custom data related to the add-on.'
          )
          .optional(),
        tags: z
          .object({
            privateTags: z
              .object({ tagIds: z.array(z.string()).max(100).optional() })
              .describe(
                'Tags that require an additional permission in order to access them, normally not given to site members or visitors.'
              )
              .optional(),
            tags: z
              .object({ tagIds: z.array(z.string()).max(100).optional() })
              .describe(
                'Tags that are exposed to anyone who has access to the labeled entity itself, including site members and visitors.'
              )
              .optional(),
          })
          .describe(
            '[Tags](https://dev.wix.com/docs/rest/business-management/tags/introduction) assigned to add-ons for categorization and filtering purposes.'
          )
          .optional(),
      }),
      z.xor([
        z.object({
          durationInMinutes: z.never().optional(),
          maxQuantity: z.never().optional(),
        }),
        z.object({
          maxQuantity: z.never().optional(),
          durationInMinutes: z
            .number()
            .int()
            .describe(
              'Duration in minutes for duration-based add-ons that extend offering time.'
            )
            .min(1)
            .max(1440),
        }),
        z.object({
          durationInMinutes: z.never().optional(),
          maxQuantity: z
            .number()
            .int()
            .describe(
              'Maximum quantity customers can purchase for quantity-based add-ons.'
            )
            .min(1)
            .max(9999),
        }),
      ])
    )
    .describe('Add-on to create.'),
});
export const CreateAddOnResponse = z.intersection(
  z.object({
    _id: z
      .string()
      .describe('Add-on ID.')
      .regex(
        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
        'Must be a valid GUID'
      )
      .optional()
      .nullable(),
    revision: z
      .string()
      .regex(/^-?\d+$/, 'Must be a valid Int64 string')
      .describe(
        'Revision number, which increments by 1 each time the add-on is updated.\nTo prevent conflicting changes, the current revision must be passed when updating the add-on.\n\nIgnored when creating an add-on.'
      )
      .optional()
      .nullable(),
    _createdDate: z
      .date()
      .describe(
        'Date and time the add-on was created in `YYYY-MM-DDThh:mm:ss.sssZ` format.'
      )
      .optional()
      .nullable(),
    _updatedDate: z
      .date()
      .describe(
        'Date and time the add-on was last updated in `YYYY-MM-DDThh:mm:ss.sssZ` format.'
      )
      .optional()
      .nullable(),
    name: z
      .string()
      .describe('Display name of the add-on shown to customers.')
      .min(1)
      .max(100)
      .optional()
      .nullable(),
    price: z
      .object({
        value: z
          .string()
          .describe(
            'Add-on price amount as a decimal string with period as decimal separator. For example, `3.99` for a $3.99 add-on or `15.00` for a $15.00 add-on.'
          )
          .optional(),
        currency: z
          .string()
          .describe(
            'Currency code for the add-on price. Must be a valid [ISO 4217](https://en.wikipedia.org/wiki/ISO_4217) currency code. For example, `USD` for US dollars or `EUR` for euros.'
          )
          .optional(),
        formattedValue: z
          .string()
          .describe(
            'Add-on price amount as a decimal string in local format for display purposes. For example, `$3.99` for US format or `3,99 €` for European format.'
          )
          .max(50)
          .optional()
          .nullable(),
      })
      .describe('Pricing information for the add-on.')
      .optional(),
    taxGroup: z
      .string()
      .describe(
        "ID of the [tax group](https://dev.wix.com/docs/rest/business-solutions/e-commerce/extensions/tax/tax-groups/introduction) to categorize the add-on for tax calculation in [Wix eCommerce](https://dev.wix.com/docs/rest/business-solutions/e-commerce/introduction).\nLearn more about [how Wix eCommerce implements tax calculation](https://dev.wix.com/docs/rest/business-solutions/e-commerce/extensions/tax/introduction).\n\nThis field doesn't apply to custom checkout/billing implementations."
      )
      .regex(
        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
        'Must be a valid GUID'
      )
      .optional()
      .nullable(),
    extendedFields: z
      .object({
        namespaces: z
          .record(z.string(), z.record(z.string(), z.any()))
          .describe(
            'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
          )
          .optional(),
      })
      .describe(
        'Data extensions enabling users to save custom data related to the add-on.'
      )
      .optional(),
    tags: z
      .object({
        privateTags: z
          .object({ tagIds: z.array(z.string()).max(100).optional() })
          .describe(
            'Tags that require an additional permission in order to access them, normally not given to site members or visitors.'
          )
          .optional(),
        tags: z
          .object({ tagIds: z.array(z.string()).max(100).optional() })
          .describe(
            'Tags that are exposed to anyone who has access to the labeled entity itself, including site members and visitors.'
          )
          .optional(),
      })
      .describe(
        '[Tags](https://dev.wix.com/docs/rest/business-management/tags/introduction) assigned to add-ons for categorization and filtering purposes.'
      )
      .optional(),
  }),
  z.xor([
    z.object({
      durationInMinutes: z.never().optional(),
      maxQuantity: z.never().optional(),
    }),
    z.object({
      maxQuantity: z.never().optional(),
      durationInMinutes: z
        .number()
        .int()
        .describe(
          'Duration in minutes for duration-based add-ons that extend offering time.'
        )
        .min(1)
        .max(1440),
    }),
    z.object({
      durationInMinutes: z.never().optional(),
      maxQuantity: z
        .number()
        .int()
        .describe(
          'Maximum quantity customers can purchase for quantity-based add-ons.'
        )
        .min(1)
        .max(9999),
    }),
  ])
);
export const GetAddOnRequest = z.object({
  addOnId: z
    .string()
    .describe('ID of the add-on to retrieve.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
});
export const GetAddOnResponse = z.intersection(
  z.object({
    _id: z
      .string()
      .describe('Add-on ID.')
      .regex(
        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
        'Must be a valid GUID'
      )
      .optional()
      .nullable(),
    revision: z
      .string()
      .regex(/^-?\d+$/, 'Must be a valid Int64 string')
      .describe(
        'Revision number, which increments by 1 each time the add-on is updated.\nTo prevent conflicting changes, the current revision must be passed when updating the add-on.\n\nIgnored when creating an add-on.'
      )
      .optional()
      .nullable(),
    _createdDate: z
      .date()
      .describe(
        'Date and time the add-on was created in `YYYY-MM-DDThh:mm:ss.sssZ` format.'
      )
      .optional()
      .nullable(),
    _updatedDate: z
      .date()
      .describe(
        'Date and time the add-on was last updated in `YYYY-MM-DDThh:mm:ss.sssZ` format.'
      )
      .optional()
      .nullable(),
    name: z
      .string()
      .describe('Display name of the add-on shown to customers.')
      .min(1)
      .max(100)
      .optional()
      .nullable(),
    price: z
      .object({
        value: z
          .string()
          .describe(
            'Add-on price amount as a decimal string with period as decimal separator. For example, `3.99` for a $3.99 add-on or `15.00` for a $15.00 add-on.'
          )
          .optional(),
        currency: z
          .string()
          .describe(
            'Currency code for the add-on price. Must be a valid [ISO 4217](https://en.wikipedia.org/wiki/ISO_4217) currency code. For example, `USD` for US dollars or `EUR` for euros.'
          )
          .optional(),
        formattedValue: z
          .string()
          .describe(
            'Add-on price amount as a decimal string in local format for display purposes. For example, `$3.99` for US format or `3,99 €` for European format.'
          )
          .max(50)
          .optional()
          .nullable(),
      })
      .describe('Pricing information for the add-on.')
      .optional(),
    taxGroup: z
      .string()
      .describe(
        "ID of the [tax group](https://dev.wix.com/docs/rest/business-solutions/e-commerce/extensions/tax/tax-groups/introduction) to categorize the add-on for tax calculation in [Wix eCommerce](https://dev.wix.com/docs/rest/business-solutions/e-commerce/introduction).\nLearn more about [how Wix eCommerce implements tax calculation](https://dev.wix.com/docs/rest/business-solutions/e-commerce/extensions/tax/introduction).\n\nThis field doesn't apply to custom checkout/billing implementations."
      )
      .regex(
        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
        'Must be a valid GUID'
      )
      .optional()
      .nullable(),
    extendedFields: z
      .object({
        namespaces: z
          .record(z.string(), z.record(z.string(), z.any()))
          .describe(
            'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
          )
          .optional(),
      })
      .describe(
        'Data extensions enabling users to save custom data related to the add-on.'
      )
      .optional(),
    tags: z
      .object({
        privateTags: z
          .object({ tagIds: z.array(z.string()).max(100).optional() })
          .describe(
            'Tags that require an additional permission in order to access them, normally not given to site members or visitors.'
          )
          .optional(),
        tags: z
          .object({ tagIds: z.array(z.string()).max(100).optional() })
          .describe(
            'Tags that are exposed to anyone who has access to the labeled entity itself, including site members and visitors.'
          )
          .optional(),
      })
      .describe(
        '[Tags](https://dev.wix.com/docs/rest/business-management/tags/introduction) assigned to add-ons for categorization and filtering purposes.'
      )
      .optional(),
  }),
  z.xor([
    z.object({
      durationInMinutes: z.never().optional(),
      maxQuantity: z.never().optional(),
    }),
    z.object({
      maxQuantity: z.never().optional(),
      durationInMinutes: z
        .number()
        .int()
        .describe(
          'Duration in minutes for duration-based add-ons that extend offering time.'
        )
        .min(1)
        .max(1440),
    }),
    z.object({
      durationInMinutes: z.never().optional(),
      maxQuantity: z
        .number()
        .int()
        .describe(
          'Maximum quantity customers can purchase for quantity-based add-ons.'
        )
        .min(1)
        .max(9999),
    }),
  ])
);
export const UpdateAddOnRequest = z.object({
  _id: z
    .string()
    .describe('Add-on ID.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
  addOn: z
    .intersection(
      z.object({
        _id: z
          .string()
          .describe('Add-on ID.')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        revision: z
          .string()
          .regex(/^-?\d+$/, 'Must be a valid Int64 string')
          .describe(
            'Revision number, which increments by 1 each time the add-on is updated.\nTo prevent conflicting changes, the current revision must be passed when updating the add-on.\n\nIgnored when creating an add-on.'
          ),
        _createdDate: z
          .date()
          .describe(
            'Date and time the add-on was created in `YYYY-MM-DDThh:mm:ss.sssZ` format.'
          )
          .optional()
          .nullable(),
        _updatedDate: z
          .date()
          .describe(
            'Date and time the add-on was last updated in `YYYY-MM-DDThh:mm:ss.sssZ` format.'
          )
          .optional()
          .nullable(),
        name: z
          .string()
          .describe('Display name of the add-on shown to customers.')
          .min(1)
          .max(100)
          .optional()
          .nullable(),
        price: z
          .object({
            value: z
              .string()
              .describe(
                'Add-on price amount as a decimal string with period as decimal separator. For example, `3.99` for a $3.99 add-on or `15.00` for a $15.00 add-on.'
              )
              .optional(),
            currency: z
              .string()
              .describe(
                'Currency code for the add-on price. Must be a valid [ISO 4217](https://en.wikipedia.org/wiki/ISO_4217) currency code. For example, `USD` for US dollars or `EUR` for euros.'
              )
              .optional(),
            formattedValue: z
              .string()
              .describe(
                'Add-on price amount as a decimal string in local format for display purposes. For example, `$3.99` for US format or `3,99 €` for European format.'
              )
              .max(50)
              .optional()
              .nullable(),
          })
          .describe('Pricing information for the add-on.')
          .optional(),
        taxGroup: z
          .string()
          .describe(
            "ID of the [tax group](https://dev.wix.com/docs/rest/business-solutions/e-commerce/extensions/tax/tax-groups/introduction) to categorize the add-on for tax calculation in [Wix eCommerce](https://dev.wix.com/docs/rest/business-solutions/e-commerce/introduction).\nLearn more about [how Wix eCommerce implements tax calculation](https://dev.wix.com/docs/rest/business-solutions/e-commerce/extensions/tax/introduction).\n\nThis field doesn't apply to custom checkout/billing implementations."
          )
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        extendedFields: z
          .object({
            namespaces: z
              .record(z.string(), z.record(z.string(), z.any()))
              .describe(
                'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
              )
              .optional(),
          })
          .describe(
            'Data extensions enabling users to save custom data related to the add-on.'
          )
          .optional(),
        tags: z
          .object({
            privateTags: z
              .object({ tagIds: z.array(z.string()).max(100).optional() })
              .describe(
                'Tags that require an additional permission in order to access them, normally not given to site members or visitors.'
              )
              .optional(),
            tags: z
              .object({ tagIds: z.array(z.string()).max(100).optional() })
              .describe(
                'Tags that are exposed to anyone who has access to the labeled entity itself, including site members and visitors.'
              )
              .optional(),
          })
          .describe(
            '[Tags](https://dev.wix.com/docs/rest/business-management/tags/introduction) assigned to add-ons for categorization and filtering purposes.'
          )
          .optional(),
      }),
      z.xor([
        z.object({
          durationInMinutes: z.never().optional(),
          maxQuantity: z.never().optional(),
        }),
        z.object({
          maxQuantity: z.never().optional(),
          durationInMinutes: z
            .number()
            .int()
            .describe(
              'Duration in minutes for duration-based add-ons that extend offering time.'
            )
            .min(1)
            .max(1440),
        }),
        z.object({
          durationInMinutes: z.never().optional(),
          maxQuantity: z
            .number()
            .int()
            .describe(
              'Maximum quantity customers can purchase for quantity-based add-ons.'
            )
            .min(1)
            .max(9999),
        }),
      ])
    )
    .describe('Add-on to update.'),
});
export const UpdateAddOnResponse = z.intersection(
  z.object({
    _id: z
      .string()
      .describe('Add-on ID.')
      .regex(
        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
        'Must be a valid GUID'
      )
      .optional()
      .nullable(),
    revision: z
      .string()
      .regex(/^-?\d+$/, 'Must be a valid Int64 string')
      .describe(
        'Revision number, which increments by 1 each time the add-on is updated.\nTo prevent conflicting changes, the current revision must be passed when updating the add-on.\n\nIgnored when creating an add-on.'
      )
      .optional()
      .nullable(),
    _createdDate: z
      .date()
      .describe(
        'Date and time the add-on was created in `YYYY-MM-DDThh:mm:ss.sssZ` format.'
      )
      .optional()
      .nullable(),
    _updatedDate: z
      .date()
      .describe(
        'Date and time the add-on was last updated in `YYYY-MM-DDThh:mm:ss.sssZ` format.'
      )
      .optional()
      .nullable(),
    name: z
      .string()
      .describe('Display name of the add-on shown to customers.')
      .min(1)
      .max(100)
      .optional()
      .nullable(),
    price: z
      .object({
        value: z
          .string()
          .describe(
            'Add-on price amount as a decimal string with period as decimal separator. For example, `3.99` for a $3.99 add-on or `15.00` for a $15.00 add-on.'
          )
          .optional(),
        currency: z
          .string()
          .describe(
            'Currency code for the add-on price. Must be a valid [ISO 4217](https://en.wikipedia.org/wiki/ISO_4217) currency code. For example, `USD` for US dollars or `EUR` for euros.'
          )
          .optional(),
        formattedValue: z
          .string()
          .describe(
            'Add-on price amount as a decimal string in local format for display purposes. For example, `$3.99` for US format or `3,99 €` for European format.'
          )
          .max(50)
          .optional()
          .nullable(),
      })
      .describe('Pricing information for the add-on.')
      .optional(),
    taxGroup: z
      .string()
      .describe(
        "ID of the [tax group](https://dev.wix.com/docs/rest/business-solutions/e-commerce/extensions/tax/tax-groups/introduction) to categorize the add-on for tax calculation in [Wix eCommerce](https://dev.wix.com/docs/rest/business-solutions/e-commerce/introduction).\nLearn more about [how Wix eCommerce implements tax calculation](https://dev.wix.com/docs/rest/business-solutions/e-commerce/extensions/tax/introduction).\n\nThis field doesn't apply to custom checkout/billing implementations."
      )
      .regex(
        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
        'Must be a valid GUID'
      )
      .optional()
      .nullable(),
    extendedFields: z
      .object({
        namespaces: z
          .record(z.string(), z.record(z.string(), z.any()))
          .describe(
            'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
          )
          .optional(),
      })
      .describe(
        'Data extensions enabling users to save custom data related to the add-on.'
      )
      .optional(),
    tags: z
      .object({
        privateTags: z
          .object({ tagIds: z.array(z.string()).max(100).optional() })
          .describe(
            'Tags that require an additional permission in order to access them, normally not given to site members or visitors.'
          )
          .optional(),
        tags: z
          .object({ tagIds: z.array(z.string()).max(100).optional() })
          .describe(
            'Tags that are exposed to anyone who has access to the labeled entity itself, including site members and visitors.'
          )
          .optional(),
      })
      .describe(
        '[Tags](https://dev.wix.com/docs/rest/business-management/tags/introduction) assigned to add-ons for categorization and filtering purposes.'
      )
      .optional(),
  }),
  z.xor([
    z.object({
      durationInMinutes: z.never().optional(),
      maxQuantity: z.never().optional(),
    }),
    z.object({
      maxQuantity: z.never().optional(),
      durationInMinutes: z
        .number()
        .int()
        .describe(
          'Duration in minutes for duration-based add-ons that extend offering time.'
        )
        .min(1)
        .max(1440),
    }),
    z.object({
      durationInMinutes: z.never().optional(),
      maxQuantity: z
        .number()
        .int()
        .describe(
          'Maximum quantity customers can purchase for quantity-based add-ons.'
        )
        .min(1)
        .max(9999),
    }),
  ])
);
export const DeleteAddOnRequest = z.object({
  addOnId: z
    .string()
    .describe('ID of the add-on to delete.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
});
export const DeleteAddOnResponse = z.object({});
export const QueryAddOnsRequest = z.object({
  query: z
    .intersection(
      z.object({
        filter: z
          .record(z.string(), z.any())
          .describe(
            'Filter object in the following format:\n`"filter" : {\n"fieldName1": "value1",\n"fieldName2":{"$operator":"value2"}\n}`\nExample of operators: `$eq`, `$ne`, `$lt`, `$lte`, `$gt`, `$gte`, `$in`, `$hasSome`, `$hasAll`, `$startsWith`, `$contains`\n\nRefer to [supported filters](https://dev.wix.com/docs/rest/business-solutions/bookings/services/add-ons/supported-filters) for a complete list of supported fields.'
          )
          .optional()
          .nullable(),
        sort: z
          .array(
            z.object({
              fieldName: z
                .string()
                .describe('Name of the field to sort by.')
                .max(512)
                .optional(),
              order: z.enum(['ASC', 'DESC']).optional(),
            })
          )
          .max(5)
          .optional(),
      }),
      z.xor([
        z.object({ cursorPaging: z.never().optional() }),
        z.object({
          cursorPaging: z
            .object({
              limit: z
                .number()
                .int()
                .describe('Maximum number of items to return in the results.')
                .min(0)
                .max(100)
                .optional()
                .nullable(),
              cursor: z
                .string()
                .describe(
                  "Pointer to the next or previous page in the list of results.\n\nPass the relevant cursor token from the `pagingMetadata` object in the previous call's response.\nNot relevant for the first request."
                )
                .max(16000)
                .optional()
                .nullable(),
            })
            .describe(
              'Cursor token pointing to a page of results. Not used in the first request. Following requests use the cursor token and not `filter` or `sort`.'
            ),
        }),
      ])
    )
    .describe('Filtering, sorting, and paging details.'),
});
export const QueryAddOnsResponse = z.object({
  addOns: z
    .array(
      z.intersection(
        z.object({
          _id: z
            .string()
            .describe('Add-on ID.')
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional()
            .nullable(),
          revision: z
            .string()
            .regex(/^-?\d+$/, 'Must be a valid Int64 string')
            .describe(
              'Revision number, which increments by 1 each time the add-on is updated.\nTo prevent conflicting changes, the current revision must be passed when updating the add-on.\n\nIgnored when creating an add-on.'
            )
            .optional()
            .nullable(),
          _createdDate: z
            .date()
            .describe(
              'Date and time the add-on was created in `YYYY-MM-DDThh:mm:ss.sssZ` format.'
            )
            .optional()
            .nullable(),
          _updatedDate: z
            .date()
            .describe(
              'Date and time the add-on was last updated in `YYYY-MM-DDThh:mm:ss.sssZ` format.'
            )
            .optional()
            .nullable(),
          name: z
            .string()
            .describe('Display name of the add-on shown to customers.')
            .min(1)
            .max(100)
            .optional()
            .nullable(),
          price: z
            .object({
              value: z
                .string()
                .describe(
                  'Add-on price amount as a decimal string with period as decimal separator. For example, `3.99` for a $3.99 add-on or `15.00` for a $15.00 add-on.'
                )
                .optional(),
              currency: z
                .string()
                .describe(
                  'Currency code for the add-on price. Must be a valid [ISO 4217](https://en.wikipedia.org/wiki/ISO_4217) currency code. For example, `USD` for US dollars or `EUR` for euros.'
                )
                .optional(),
              formattedValue: z
                .string()
                .describe(
                  'Add-on price amount as a decimal string in local format for display purposes. For example, `$3.99` for US format or `3,99 €` for European format.'
                )
                .max(50)
                .optional()
                .nullable(),
            })
            .describe('Pricing information for the add-on.')
            .optional(),
          taxGroup: z
            .string()
            .describe(
              "ID of the [tax group](https://dev.wix.com/docs/rest/business-solutions/e-commerce/extensions/tax/tax-groups/introduction) to categorize the add-on for tax calculation in [Wix eCommerce](https://dev.wix.com/docs/rest/business-solutions/e-commerce/introduction).\nLearn more about [how Wix eCommerce implements tax calculation](https://dev.wix.com/docs/rest/business-solutions/e-commerce/extensions/tax/introduction).\n\nThis field doesn't apply to custom checkout/billing implementations."
            )
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional()
            .nullable(),
          extendedFields: z
            .object({
              namespaces: z
                .record(z.string(), z.record(z.string(), z.any()))
                .describe(
                  'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
                )
                .optional(),
            })
            .describe(
              'Data extensions enabling users to save custom data related to the add-on.'
            )
            .optional(),
          tags: z
            .object({
              privateTags: z
                .object({ tagIds: z.array(z.string()).max(100).optional() })
                .describe(
                  'Tags that require an additional permission in order to access them, normally not given to site members or visitors.'
                )
                .optional(),
              tags: z
                .object({ tagIds: z.array(z.string()).max(100).optional() })
                .describe(
                  'Tags that are exposed to anyone who has access to the labeled entity itself, including site members and visitors.'
                )
                .optional(),
            })
            .describe(
              '[Tags](https://dev.wix.com/docs/rest/business-management/tags/introduction) assigned to add-ons for categorization and filtering purposes.'
            )
            .optional(),
        }),
        z.xor([
          z.object({
            durationInMinutes: z.never().optional(),
            maxQuantity: z.never().optional(),
          }),
          z.object({
            maxQuantity: z.never().optional(),
            durationInMinutes: z
              .number()
              .int()
              .describe(
                'Duration in minutes for duration-based add-ons that extend offering time.'
              )
              .min(1)
              .max(1440),
          }),
          z.object({
            durationInMinutes: z.never().optional(),
            maxQuantity: z
              .number()
              .int()
              .describe(
                'Maximum quantity customers can purchase for quantity-based add-ons.'
              )
              .min(1)
              .max(9999),
          }),
        ])
      )
    )
    .optional(),
  pagingMetadata: z
    .object({
      count: z
        .number()
        .int()
        .describe('Number of items returned in the response.')
        .optional()
        .nullable(),
      cursors: z
        .object({
          next: z
            .string()
            .describe(
              'Cursor string pointing to the next page in the list of results.'
            )
            .max(16000)
            .optional()
            .nullable(),
          prev: z
            .string()
            .describe(
              'Cursor pointing to the previous page in the list of results.'
            )
            .max(16000)
            .optional()
            .nullable(),
        })
        .describe(
          'Cursor strings that point to the next page, previous page, or both.'
        )
        .optional(),
      hasNext: z
        .boolean()
        .describe(
          'Whether there are more pages to retrieve following the current page.\n\n+ `true`: Another page of results can be retrieved.\n+ `false`: This is the last page.'
        )
        .optional()
        .nullable(),
    })
    .describe('Paging metadata for the query results.')
    .optional(),
});
export const BulkCreateAddOnsRequest = z.object({
  addOns: z
    .array(
      z.intersection(
        z.object({
          _id: z
            .string()
            .describe('Add-on ID.')
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional()
            .nullable(),
          revision: z
            .string()
            .regex(/^-?\d+$/, 'Must be a valid Int64 string')
            .describe(
              'Revision number, which increments by 1 each time the add-on is updated.\nTo prevent conflicting changes, the current revision must be passed when updating the add-on.\n\nIgnored when creating an add-on.'
            )
            .optional()
            .nullable(),
          _createdDate: z
            .date()
            .describe(
              'Date and time the add-on was created in `YYYY-MM-DDThh:mm:ss.sssZ` format.'
            )
            .optional()
            .nullable(),
          _updatedDate: z
            .date()
            .describe(
              'Date and time the add-on was last updated in `YYYY-MM-DDThh:mm:ss.sssZ` format.'
            )
            .optional()
            .nullable(),
          name: z
            .string()
            .describe('Display name of the add-on shown to customers.')
            .min(1)
            .max(100)
            .optional()
            .nullable(),
          price: z
            .object({
              value: z
                .string()
                .describe(
                  'Add-on price amount as a decimal string with period as decimal separator. For example, `3.99` for a $3.99 add-on or `15.00` for a $15.00 add-on.'
                )
                .optional(),
              currency: z
                .string()
                .describe(
                  'Currency code for the add-on price. Must be a valid [ISO 4217](https://en.wikipedia.org/wiki/ISO_4217) currency code. For example, `USD` for US dollars or `EUR` for euros.'
                )
                .optional(),
              formattedValue: z
                .string()
                .describe(
                  'Add-on price amount as a decimal string in local format for display purposes. For example, `$3.99` for US format or `3,99 €` for European format.'
                )
                .max(50)
                .optional()
                .nullable(),
            })
            .describe('Pricing information for the add-on.')
            .optional(),
          taxGroup: z
            .string()
            .describe(
              "ID of the [tax group](https://dev.wix.com/docs/rest/business-solutions/e-commerce/extensions/tax/tax-groups/introduction) to categorize the add-on for tax calculation in [Wix eCommerce](https://dev.wix.com/docs/rest/business-solutions/e-commerce/introduction).\nLearn more about [how Wix eCommerce implements tax calculation](https://dev.wix.com/docs/rest/business-solutions/e-commerce/extensions/tax/introduction).\n\nThis field doesn't apply to custom checkout/billing implementations."
            )
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional()
            .nullable(),
          extendedFields: z
            .object({
              namespaces: z
                .record(z.string(), z.record(z.string(), z.any()))
                .describe(
                  'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
                )
                .optional(),
            })
            .describe(
              'Data extensions enabling users to save custom data related to the add-on.'
            )
            .optional(),
          tags: z
            .object({
              privateTags: z
                .object({ tagIds: z.array(z.string()).max(100).optional() })
                .describe(
                  'Tags that require an additional permission in order to access them, normally not given to site members or visitors.'
                )
                .optional(),
              tags: z
                .object({ tagIds: z.array(z.string()).max(100).optional() })
                .describe(
                  'Tags that are exposed to anyone who has access to the labeled entity itself, including site members and visitors.'
                )
                .optional(),
            })
            .describe(
              '[Tags](https://dev.wix.com/docs/rest/business-management/tags/introduction) assigned to add-ons for categorization and filtering purposes.'
            )
            .optional(),
        }),
        z.xor([
          z.object({
            durationInMinutes: z.never().optional(),
            maxQuantity: z.never().optional(),
          }),
          z.object({
            maxQuantity: z.never().optional(),
            durationInMinutes: z
              .number()
              .int()
              .describe(
                'Duration in minutes for duration-based add-ons that extend offering time.'
              )
              .min(1)
              .max(1440),
          }),
          z.object({
            durationInMinutes: z.never().optional(),
            maxQuantity: z
              .number()
              .int()
              .describe(
                'Maximum quantity customers can purchase for quantity-based add-ons.'
              )
              .min(1)
              .max(9999),
          }),
        ])
      )
    )
    .min(1)
    .max(100),
  options: z
    .object({
      returnEntity: z
        .boolean()
        .describe('Whether to return the created add-ons in the response.')
        .optional(),
    })
    .optional(),
});
export const BulkCreateAddOnsResponse = z.object({
  results: z
    .array(
      z.object({
        itemMetadata: z
          .object({
            _id: z
              .string()
              .describe(
                "Item ID. Should always be available, unless it's impossible (for example, when failing to create an item)."
              )
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            originalIndex: z
              .number()
              .int()
              .describe(
                'Index of the item within the request array. Allows for correlation between request and response items.'
              )
              .optional(),
            success: z
              .boolean()
              .describe(
                'Whether the requested action was successful for this item. When `false`, the `error` field is populated.'
              )
              .optional(),
            error: z
              .object({
                code: z.string().describe('Error code.').optional(),
                description: z
                  .string()
                  .describe('Description of the error.')
                  .optional(),
                data: z
                  .record(z.string(), z.any())
                  .describe('Data related to the error.')
                  .optional()
                  .nullable(),
              })
              .describe('Details about the error in case of failure.')
              .optional(),
          })
          .describe('Metadata for the individual create operation.')
          .optional(),
        item: z
          .intersection(
            z.object({
              _id: z
                .string()
                .describe('Add-on ID.')
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional()
                .nullable(),
              revision: z
                .string()
                .regex(/^-?\d+$/, 'Must be a valid Int64 string')
                .describe(
                  'Revision number, which increments by 1 each time the add-on is updated.\nTo prevent conflicting changes, the current revision must be passed when updating the add-on.\n\nIgnored when creating an add-on.'
                )
                .optional()
                .nullable(),
              _createdDate: z
                .date()
                .describe(
                  'Date and time the add-on was created in `YYYY-MM-DDThh:mm:ss.sssZ` format.'
                )
                .optional()
                .nullable(),
              _updatedDate: z
                .date()
                .describe(
                  'Date and time the add-on was last updated in `YYYY-MM-DDThh:mm:ss.sssZ` format.'
                )
                .optional()
                .nullable(),
              name: z
                .string()
                .describe('Display name of the add-on shown to customers.')
                .min(1)
                .max(100)
                .optional()
                .nullable(),
              price: z
                .object({
                  value: z
                    .string()
                    .describe(
                      'Add-on price amount as a decimal string with period as decimal separator. For example, `3.99` for a $3.99 add-on or `15.00` for a $15.00 add-on.'
                    )
                    .optional(),
                  currency: z
                    .string()
                    .describe(
                      'Currency code for the add-on price. Must be a valid [ISO 4217](https://en.wikipedia.org/wiki/ISO_4217) currency code. For example, `USD` for US dollars or `EUR` for euros.'
                    )
                    .optional(),
                  formattedValue: z
                    .string()
                    .describe(
                      'Add-on price amount as a decimal string in local format for display purposes. For example, `$3.99` for US format or `3,99 €` for European format.'
                    )
                    .max(50)
                    .optional()
                    .nullable(),
                })
                .describe('Pricing information for the add-on.')
                .optional(),
              taxGroup: z
                .string()
                .describe(
                  "ID of the [tax group](https://dev.wix.com/docs/rest/business-solutions/e-commerce/extensions/tax/tax-groups/introduction) to categorize the add-on for tax calculation in [Wix eCommerce](https://dev.wix.com/docs/rest/business-solutions/e-commerce/introduction).\nLearn more about [how Wix eCommerce implements tax calculation](https://dev.wix.com/docs/rest/business-solutions/e-commerce/extensions/tax/introduction).\n\nThis field doesn't apply to custom checkout/billing implementations."
                )
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional()
                .nullable(),
              extendedFields: z
                .object({
                  namespaces: z
                    .record(z.string(), z.record(z.string(), z.any()))
                    .describe(
                      'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
                    )
                    .optional(),
                })
                .describe(
                  'Data extensions enabling users to save custom data related to the add-on.'
                )
                .optional(),
              tags: z
                .object({
                  privateTags: z
                    .object({ tagIds: z.array(z.string()).max(100).optional() })
                    .describe(
                      'Tags that require an additional permission in order to access them, normally not given to site members or visitors.'
                    )
                    .optional(),
                  tags: z
                    .object({ tagIds: z.array(z.string()).max(100).optional() })
                    .describe(
                      'Tags that are exposed to anyone who has access to the labeled entity itself, including site members and visitors.'
                    )
                    .optional(),
                })
                .describe(
                  '[Tags](https://dev.wix.com/docs/rest/business-management/tags/introduction) assigned to add-ons for categorization and filtering purposes.'
                )
                .optional(),
            }),
            z.xor([
              z.object({
                durationInMinutes: z.never().optional(),
                maxQuantity: z.never().optional(),
              }),
              z.object({
                maxQuantity: z.never().optional(),
                durationInMinutes: z
                  .number()
                  .int()
                  .describe(
                    'Duration in minutes for duration-based add-ons that extend offering time.'
                  )
                  .min(1)
                  .max(1440),
              }),
              z.object({
                durationInMinutes: z.never().optional(),
                maxQuantity: z
                  .number()
                  .int()
                  .describe(
                    'Maximum quantity customers can purchase for quantity-based add-ons.'
                  )
                  .min(1)
                  .max(9999),
              }),
            ])
          )
          .describe(
            'Created add-on. Only present if `return_entity` was set to true in the request.'
          )
          .optional(),
      })
    )
    .min(1)
    .max(100)
    .optional(),
  bulkActionMetadata: z
    .object({
      totalSuccesses: z
        .number()
        .int()
        .describe('Number of items that were successfully processed.')
        .optional(),
      totalFailures: z
        .number()
        .int()
        .describe("Number of items that couldn't be processed.")
        .optional(),
      undetailedFailures: z
        .number()
        .int()
        .describe(
          'Number of failures without details because detailed failure threshold was exceeded.'
        )
        .optional(),
    })
    .describe('Metadata for the bulk create operation.')
    .optional(),
});
export const BulkUpdateAddOnsRequest = z.object({
  addOns: z
    .array(
      z.object({
        addOn: z
          .intersection(
            z.object({
              _id: z
                .string()
                .describe('Add-on ID.')
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                ),
              revision: z
                .string()
                .regex(/^-?\d+$/, 'Must be a valid Int64 string')
                .describe(
                  'Revision number, which increments by 1 each time the add-on is updated.\nTo prevent conflicting changes, the current revision must be passed when updating the add-on.\n\nIgnored when creating an add-on.'
                ),
              _createdDate: z
                .date()
                .describe(
                  'Date and time the add-on was created in `YYYY-MM-DDThh:mm:ss.sssZ` format.'
                )
                .optional()
                .nullable(),
              _updatedDate: z
                .date()
                .describe(
                  'Date and time the add-on was last updated in `YYYY-MM-DDThh:mm:ss.sssZ` format.'
                )
                .optional()
                .nullable(),
              name: z
                .string()
                .describe('Display name of the add-on shown to customers.')
                .min(1)
                .max(100)
                .optional()
                .nullable(),
              price: z
                .object({
                  value: z
                    .string()
                    .describe(
                      'Add-on price amount as a decimal string with period as decimal separator. For example, `3.99` for a $3.99 add-on or `15.00` for a $15.00 add-on.'
                    )
                    .optional(),
                  currency: z
                    .string()
                    .describe(
                      'Currency code for the add-on price. Must be a valid [ISO 4217](https://en.wikipedia.org/wiki/ISO_4217) currency code. For example, `USD` for US dollars or `EUR` for euros.'
                    )
                    .optional(),
                  formattedValue: z
                    .string()
                    .describe(
                      'Add-on price amount as a decimal string in local format for display purposes. For example, `$3.99` for US format or `3,99 €` for European format.'
                    )
                    .max(50)
                    .optional()
                    .nullable(),
                })
                .describe('Pricing information for the add-on.')
                .optional(),
              taxGroup: z
                .string()
                .describe(
                  "ID of the [tax group](https://dev.wix.com/docs/rest/business-solutions/e-commerce/extensions/tax/tax-groups/introduction) to categorize the add-on for tax calculation in [Wix eCommerce](https://dev.wix.com/docs/rest/business-solutions/e-commerce/introduction).\nLearn more about [how Wix eCommerce implements tax calculation](https://dev.wix.com/docs/rest/business-solutions/e-commerce/extensions/tax/introduction).\n\nThis field doesn't apply to custom checkout/billing implementations."
                )
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional()
                .nullable(),
              extendedFields: z
                .object({
                  namespaces: z
                    .record(z.string(), z.record(z.string(), z.any()))
                    .describe(
                      'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
                    )
                    .optional(),
                })
                .describe(
                  'Data extensions enabling users to save custom data related to the add-on.'
                )
                .optional(),
              tags: z
                .object({
                  privateTags: z
                    .object({ tagIds: z.array(z.string()).max(100).optional() })
                    .describe(
                      'Tags that require an additional permission in order to access them, normally not given to site members or visitors.'
                    )
                    .optional(),
                  tags: z
                    .object({ tagIds: z.array(z.string()).max(100).optional() })
                    .describe(
                      'Tags that are exposed to anyone who has access to the labeled entity itself, including site members and visitors.'
                    )
                    .optional(),
                })
                .describe(
                  '[Tags](https://dev.wix.com/docs/rest/business-management/tags/introduction) assigned to add-ons for categorization and filtering purposes.'
                )
                .optional(),
            }),
            z.xor([
              z.object({
                durationInMinutes: z.never().optional(),
                maxQuantity: z.never().optional(),
              }),
              z.object({
                maxQuantity: z.never().optional(),
                durationInMinutes: z
                  .number()
                  .int()
                  .describe(
                    'Duration in minutes for duration-based add-ons that extend offering time.'
                  )
                  .min(1)
                  .max(1440),
              }),
              z.object({
                durationInMinutes: z.never().optional(),
                maxQuantity: z
                  .number()
                  .int()
                  .describe(
                    'Maximum quantity customers can purchase for quantity-based add-ons.'
                  )
                  .min(1)
                  .max(9999),
              }),
            ])
          )
          .describe('Add-on to update. May be partial.')
          .optional(),
      })
    )
    .min(1)
    .max(100),
  options: z
    .object({
      returnEntity: z
        .boolean()
        .describe('Whether to return the updated add-ons in the response.')
        .optional(),
    })
    .optional(),
});
export const BulkUpdateAddOnsResponse = z.object({
  results: z
    .array(
      z.object({
        itemMetadata: z
          .object({
            _id: z
              .string()
              .describe(
                "Item ID. Should always be available, unless it's impossible (for example, when failing to create an item)."
              )
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            originalIndex: z
              .number()
              .int()
              .describe(
                'Index of the item within the request array. Allows for correlation between request and response items.'
              )
              .optional(),
            success: z
              .boolean()
              .describe(
                'Whether the requested action was successful for this item. When `false`, the `error` field is populated.'
              )
              .optional(),
            error: z
              .object({
                code: z.string().describe('Error code.').optional(),
                description: z
                  .string()
                  .describe('Description of the error.')
                  .optional(),
                data: z
                  .record(z.string(), z.any())
                  .describe('Data related to the error.')
                  .optional()
                  .nullable(),
              })
              .describe('Details about the error in case of failure.')
              .optional(),
          })
          .describe('Metadata for the individual update operation.')
          .optional(),
        item: z
          .intersection(
            z.object({
              _id: z
                .string()
                .describe('Add-on ID.')
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional()
                .nullable(),
              revision: z
                .string()
                .regex(/^-?\d+$/, 'Must be a valid Int64 string')
                .describe(
                  'Revision number, which increments by 1 each time the add-on is updated.\nTo prevent conflicting changes, the current revision must be passed when updating the add-on.\n\nIgnored when creating an add-on.'
                )
                .optional()
                .nullable(),
              _createdDate: z
                .date()
                .describe(
                  'Date and time the add-on was created in `YYYY-MM-DDThh:mm:ss.sssZ` format.'
                )
                .optional()
                .nullable(),
              _updatedDate: z
                .date()
                .describe(
                  'Date and time the add-on was last updated in `YYYY-MM-DDThh:mm:ss.sssZ` format.'
                )
                .optional()
                .nullable(),
              name: z
                .string()
                .describe('Display name of the add-on shown to customers.')
                .min(1)
                .max(100)
                .optional()
                .nullable(),
              price: z
                .object({
                  value: z
                    .string()
                    .describe(
                      'Add-on price amount as a decimal string with period as decimal separator. For example, `3.99` for a $3.99 add-on or `15.00` for a $15.00 add-on.'
                    )
                    .optional(),
                  currency: z
                    .string()
                    .describe(
                      'Currency code for the add-on price. Must be a valid [ISO 4217](https://en.wikipedia.org/wiki/ISO_4217) currency code. For example, `USD` for US dollars or `EUR` for euros.'
                    )
                    .optional(),
                  formattedValue: z
                    .string()
                    .describe(
                      'Add-on price amount as a decimal string in local format for display purposes. For example, `$3.99` for US format or `3,99 €` for European format.'
                    )
                    .max(50)
                    .optional()
                    .nullable(),
                })
                .describe('Pricing information for the add-on.')
                .optional(),
              taxGroup: z
                .string()
                .describe(
                  "ID of the [tax group](https://dev.wix.com/docs/rest/business-solutions/e-commerce/extensions/tax/tax-groups/introduction) to categorize the add-on for tax calculation in [Wix eCommerce](https://dev.wix.com/docs/rest/business-solutions/e-commerce/introduction).\nLearn more about [how Wix eCommerce implements tax calculation](https://dev.wix.com/docs/rest/business-solutions/e-commerce/extensions/tax/introduction).\n\nThis field doesn't apply to custom checkout/billing implementations."
                )
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional()
                .nullable(),
              extendedFields: z
                .object({
                  namespaces: z
                    .record(z.string(), z.record(z.string(), z.any()))
                    .describe(
                      'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
                    )
                    .optional(),
                })
                .describe(
                  'Data extensions enabling users to save custom data related to the add-on.'
                )
                .optional(),
              tags: z
                .object({
                  privateTags: z
                    .object({ tagIds: z.array(z.string()).max(100).optional() })
                    .describe(
                      'Tags that require an additional permission in order to access them, normally not given to site members or visitors.'
                    )
                    .optional(),
                  tags: z
                    .object({ tagIds: z.array(z.string()).max(100).optional() })
                    .describe(
                      'Tags that are exposed to anyone who has access to the labeled entity itself, including site members and visitors.'
                    )
                    .optional(),
                })
                .describe(
                  '[Tags](https://dev.wix.com/docs/rest/business-management/tags/introduction) assigned to add-ons for categorization and filtering purposes.'
                )
                .optional(),
            }),
            z.xor([
              z.object({
                durationInMinutes: z.never().optional(),
                maxQuantity: z.never().optional(),
              }),
              z.object({
                maxQuantity: z.never().optional(),
                durationInMinutes: z
                  .number()
                  .int()
                  .describe(
                    'Duration in minutes for duration-based add-ons that extend offering time.'
                  )
                  .min(1)
                  .max(1440),
              }),
              z.object({
                durationInMinutes: z.never().optional(),
                maxQuantity: z
                  .number()
                  .int()
                  .describe(
                    'Maximum quantity customers can purchase for quantity-based add-ons.'
                  )
                  .min(1)
                  .max(9999),
              }),
            ])
          )
          .describe(
            'Updated add-on. Only present if `return_entity` was set to true in the request.'
          )
          .optional(),
      })
    )
    .min(1)
    .max(100)
    .optional(),
  bulkActionMetadata: z
    .object({
      totalSuccesses: z
        .number()
        .int()
        .describe('Number of items that were successfully processed.')
        .optional(),
      totalFailures: z
        .number()
        .int()
        .describe("Number of items that couldn't be processed.")
        .optional(),
      undetailedFailures: z
        .number()
        .int()
        .describe(
          'Number of failures without details because detailed failure threshold was exceeded.'
        )
        .optional(),
    })
    .describe('Metadata for the bulk update operation.')
    .optional(),
});
export const BulkDeleteAddOnsRequest = z.object({
  addOnIds: z.array(z.string()).min(1).max(100),
});
export const BulkDeleteAddOnsResponse = z.object({
  results: z
    .array(
      z.object({
        itemMetadata: z
          .object({
            _id: z
              .string()
              .describe(
                "Item ID. Should always be available, unless it's impossible (for example, when failing to create an item)."
              )
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            originalIndex: z
              .number()
              .int()
              .describe(
                'Index of the item within the request array. Allows for correlation between request and response items.'
              )
              .optional(),
            success: z
              .boolean()
              .describe(
                'Whether the requested action was successful for this item. When `false`, the `error` field is populated.'
              )
              .optional(),
            error: z
              .object({
                code: z.string().describe('Error code.').optional(),
                description: z
                  .string()
                  .describe('Description of the error.')
                  .optional(),
                data: z
                  .record(z.string(), z.any())
                  .describe('Data related to the error.')
                  .optional()
                  .nullable(),
              })
              .describe('Details about the error in case of failure.')
              .optional(),
          })
          .describe('Metadata for the individual delete operation.')
          .optional(),
      })
    )
    .min(1)
    .max(100)
    .optional(),
  bulkActionMetadata: z
    .object({
      totalSuccesses: z
        .number()
        .int()
        .describe('Number of items that were successfully processed.')
        .optional(),
      totalFailures: z
        .number()
        .int()
        .describe("Number of items that couldn't be processed.")
        .optional(),
      undetailedFailures: z
        .number()
        .int()
        .describe(
          'Number of failures without details because detailed failure threshold was exceeded.'
        )
        .optional(),
    })
    .describe('Metadata for the bulk delete operation.')
    .optional(),
});
